import { motion } from 'framer-motion';
import ProjectCard from './ProjectCard';
import RaremarqSection from './RaremarqSection';

interface ProjectGridProps {
  activeFilter: string | null;
}

const RAREMARQ_FILTER_TAGS = [
  'API Design',
  'UI/UX',
  'Branding',
  'AI/ML',
];

export default function ProjectGrid({ activeFilter }: ProjectGridProps) {
  const featuredProjects = [
    {
      id: 'rekindle',
      title: 'Rekindle',
      year: '2025',
      description:
        'A mental wellness app designed to help users reflect on emotions, connect with supportive communities, and access personalized wellness resources for emotional growth.',
      tags: ['Mobile App', 'UX Research', 'Product Design', 'Wellness'],
      filterTags: ['UI/UX'],
      imageUrl:
        'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/abd11443fa097552af62217463df360c.png',
      link: '/rekindle',
      size: 'large' as const,
      comingSoon: false,
    },
  ];

  const pairedProjects = [
    {
      id: 'shopify-web-app',
      title: 'Shopify Web App',
      year: '2025–Present',
      description:
        'Designed and developed a custom Shopify web application to streamline e-commerce operations, enhance merchant workflows, and deliver a seamless storefront experience.',
      tags: ['E-Commerce', 'Web App', 'Product Design', 'Shopify'],
      filterTags: ['UI/UX', 'Data Visualization'],
      imageUrl:
        'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/d9ff25f7f73ebae72c44729a58340bd7.png',
      link: '#',
      size: 'square' as const,
      comingSoon: true,
    },
    {
      id: 'auctionnow',
      title: 'AuctionNow.io',
      year: '2026–Present',
      description:
        'End-to-end UX design for a real-time auction platform. Built intuitive bidding flows, live countdown interfaces, and a trust-driven marketplace experience.',
      tags: ['Product Design', 'Real-Time UX', 'Web Platform'],
      filterTags: ['UI/UX', 'Data Visualization', 'API Design'],
      imageUrl:
        'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/a7a9f2ab356ce46d61d10e89f327b2c2.png',
      link: '#',
      size: 'square' as const,
      comingSoon: true,
      imageStyle: 'object-cover object-[center_10%] scale-110',
    },
  ];

  const standaloneProjects = [
    {
      id: 'internal-data-tool',
      title: 'Shopify & AuctionNow Internal Data Tool',
      year: '2025',
      description:
        'A buildoff project combining Shopify and AuctionNow data into a unified internal analytics tool. Designed dashboards for cross-platform insights, inventory tracking, and sales performance metrics.',
      tags: ['Data Visualization', 'Internal Tool', 'Analytics', 'Buildoff'],
      filterTags: ['Data Visualization', 'UI/UX'],
      imageUrl:
        'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/315fb2b767203cd240b76aa80b0564d2.png',
      link: '#',
      size: 'square' as const,
      comingSoon: true,
      imageStyle: 'object-cover object-center scale-100',
    },
    {
      id: 'sphera',
      title: 'Sphera',
      year: 'Jan – Mar 2023',
      description:
        'Product design and UX strategy for a data visualization platform. Crafting intuitive interfaces for complex datasets.',
      tags: ['Product Design', 'UX Strategy', 'Data Visualization'],
      filterTags: ['UI/UX', 'Data Visualization'],
      imageUrl:
        'https://images.squarespace-cdn.com/content/v1/63f9c6a5b04c4764ca64bd07/265ea115-1cc0-4802-bf2b-301f48ca518d/Frame+8857312.png',
      link: '#',
      size: 'square' as const,
      comingSoon: true,
      imageStyle: 'object-cover object-[center_5%] scale-110',
    },
  ];

  const bottomProjects = [
    {
      id: 'marketing-materials',
      title: 'Marketing Materials',
      year: '2024–Present',
      description:
        'Creating compelling marketing collateral including pitch decks, social media assets, and promotional materials to drive brand awareness and investor engagement.',
      tags: ['Marketing', 'Graphic Design', 'Brand Strategy'],
      filterTags: ['Branding'],
      imageUrl: 'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/dd96e85f2ccd8c7ad7ad50251eea3a78.png',
      link: '#',
      size: 'square' as const,
      comingSoon: true,
    },
    {
      id: 'merchandising-events',
      title: 'Merchandising Events',
      year: '2024–Present',
      description:
        'Designing and executing branded event experiences, booth displays, and promotional merchandise that bring the Raremarq brand to life at conventions and collector gatherings.',
      tags: ['Events', 'Merchandising', 'Brand Experience'],
      filterTags: ['Branding', 'Merchandising'],
      imageUrl: 'https://static.readdy.ai/image/7bd40147962e2e300edd3593b80ed9ab/f09d82719c0fe5303a3d2c64df1f83b2.png',
      link: '#',
      size: 'square' as const,
      comingSoon: true,
    },
  ];

  const showRaremarq =
    !activeFilter || RAREMARQ_FILTER_TAGS.includes(activeFilter);

  const filteredFeaturedProjects = activeFilter
    ? featuredProjects.filter((p) => p.filterTags.includes(activeFilter))
    : featuredProjects;

  const filteredPairedProjects = activeFilter
    ? pairedProjects.filter((p) => p.filterTags.includes(activeFilter))
    : pairedProjects;

  const filteredStandaloneProjects = activeFilter
    ? standaloneProjects.filter((p) => p.filterTags.includes(activeFilter))
    : standaloneProjects;

  const filteredBottomProjects = activeFilter
    ? bottomProjects.filter((p) => p.filterTags.includes(activeFilter))
    : bottomProjects;

  const hasResults = showRaremarq || filteredFeaturedProjects.length > 0 || filteredPairedProjects.length > 0 || filteredStandaloneProjects.length > 0 || filteredBottomProjects.length > 0;

  return (
    <div id="portfolio" className="space-y-8">
      {/* Section Header */}
      <div className="flex items-center gap-2">
        <span className="w-5 h-5 flex items-center justify-center text-notion-secondary">
          <i className="ri-folder-line text-base"></i>
        </span>
        <h2 className="font-serif text-lg font-medium text-notion-text">
          Portfolio
        </h2>
      </div>

      {!hasResults && (
        <div className="py-12 text-center">
          <span className="w-10 h-10 flex items-center justify-center mx-auto text-notion-secondary/40 mb-3">
            <i className="ri-search-line text-2xl"></i>
          </span>
          <p className="text-[14px] text-notion-secondary">
            No projects match this filter.
          </p>
        </div>
      )}

      {/* Raremarq Parent Section */}
      {showRaremarq && <RaremarqSection activeFilter={activeFilter} />}

      {/* Featured Projects: Rekindle */}
      {filteredFeaturedProjects.length > 0 && (
        <motion.div
          className="grid grid-cols-1 gap-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          {filteredFeaturedProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      )}

      {/* Paired Projects: Shopify & AuctionNow - 2 column grid */}
      {filteredPairedProjects.length > 0 && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25, duration: 0.4 }}
        >
          {filteredPairedProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      )}

      {/* Standalone Projects: Internal Data Tool & Sphera - 2 column grid */}
      {filteredStandaloneProjects.length > 0 && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.4 }}
        >
          {filteredStandaloneProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      )}

      {/* Bottom Projects: Marketing Materials & Merchandising Events - 2 column grid */}
      {filteredBottomProjects.length > 0 && (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.4 }}
        >
          {filteredBottomProjects.map((project) => (
            <ProjectCard key={project.id} project={project} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
